import { isEqual } from 'lodash';
import { create } from 'zustand';
import { paths } from '@common/types';
import { IMyTasksFilters } from '../types';

type Priority = NonNullable<
  NonNullable<paths['/task']['get']['parameters']['query']>['priorities']
>[number];

export type WorklistTabIndexType =
  'all' | 'urgent' | 'inProgress' | 'notStarted' | 'completed';

const myWorklistTabIndex: Record<WorklistTabIndexType, WorklistTabIndexType> = {
  all: 'all',
  urgent: 'urgent',
  inProgress: 'inProgress',
  notStarted: 'notStarted',
  completed: 'completed',
};

const initialPriorityList: Priority[] = [
  'Overdue',
  'Urgent',
  'High',
  'Medium',
  'Low',
];

const initialUrgentPriorityList: Priority[] = ['Overdue', 'Urgent'];

const getDefaultFilters = (): IMyTasksFilters => ({
  search: '',
  programs: [],
  due_date: '',
  category: '',
  team_id: '',
  assignee_id: '',
  location_building_ids: [],
  priorities: initialPriorityList,
  sort_by: 'due_date',
  sort_order: 'ASC',
  page: 1,
  limit: 100,
  own_tasks_only: true,
  statuses: ['Pending', 'In progress'],
  practice_id: '',
  last_updated_after: '',
  order_categories: [],
  order_types: [],
  task_names: [],
  partner_id: '',
  provider_ids: [],
  appliedFilters: 0,
});

type TKeyOfFilters = keyof IMyTasksFilters;

const EXCLUDED_FILTER_KEYS: TKeyOfFilters[] = [
  'page',
  'limit',
  'search',
  'sort_order',
  'sort_by',
  'appliedFilters',
  'provider_ids',
  'partner_id',
  'own_tasks_only',
];

const APPLY_ENABLED_EXCLUDED_KEYS: TKeyOfFilters[] = [
  'search',
  'own_tasks_only',
  'partner_id',
  'provider_ids',
];

const getIsApplyEnabled = (
  filters: IMyTasksFilters,
  draftFilters: IMyTasksFilters,
): boolean => {
  const keys = (Object.keys(filters) as TKeyOfFilters[]).filter(
    key => !APPLY_ENABLED_EXCLUDED_KEYS.includes(key),
  );
  return keys.some(key => !isEqual(filters[key], draftFilters[key]));
};

const getTabDefaults = (tabIndex: WorklistTabIndexType): IMyTasksFilters => {
  const defaults = getDefaultFilters();
  if (tabIndex === myWorklistTabIndex.urgent) {
    return { ...defaults, priorities: initialUrgentPriorityList };
  }
  return defaults;
};

const getIsResetEnabled = (
  filters: IMyTasksFilters,
  tabIndex: WorklistTabIndexType,
): boolean => {
  const tabDefaults = getTabDefaults(tabIndex);
  const keys = (Object.keys(filters) as TKeyOfFilters[]).filter(
    key => !APPLY_ENABLED_EXCLUDED_KEYS.includes(key),
  );
  return keys.some(key => !isEqual(filters[key], tabDefaults[key]));
};

const getAppliedFiltersCount = (
  defaultFilters: IMyTasksFilters,
  appliedFilters: IMyTasksFilters,
): number => {
  const filterKeys = (Object.keys(appliedFilters) as TKeyOfFilters[]).filter(
    key => !EXCLUDED_FILTER_KEYS.includes(key),
  );

  return filterKeys.reduce((count, key) => {
    return isEqual(defaultFilters[key], appliedFilters[key])
      ? count
      : count + 1;
  }, 0);
};

type TMyWorklistFilterStore = {
  tabIndex: WorklistTabIndexType;
  setTabIndex: (tabIndex: WorklistTabIndexType) => void;
  isFiltersCardOpen: boolean;
  handleSetIsFiltersCardOpen: () => void;
  handleSetIsFiltersCardClose: () => void;
  handleToggleFiltersCard: () => void;

  filters: IMyTasksFilters;
  draftFilters: IMyTasksFilters;
  setFilters: (newFilters: Partial<IMyTasksFilters>) => void;
  setDraftFilters: (newFilters: Partial<IMyTasksFilters>) => void;

  applyDraftFilters: () => void;
  resetFilters: () => void;
  resetAllFilters: () => void;
  resetDraftFilters: () => void;

  syncDraftWithAppliedFilters: () => void;
  appliedFiltersCount: number;
  draftFiltersCount: number;
  isInitialFilters: boolean;
  isApplyEnabled: boolean;
  isResetEnabled: boolean;
  myWorklistTabIndex: Record<WorklistTabIndexType, WorklistTabIndexType>;

  clearStore: () => void;
};

export const useMyWorklistFilterStore = create<TMyWorklistFilterStore>()(
  set => ({
    tabIndex: myWorklistTabIndex.all,
    setTabIndex: tabIndex => {
      set(state => {
        const defaults = getTabDefaults(tabIndex);
        const updatedFilters = {
          ...defaults,
          priorities:
            tabIndex === myWorklistTabIndex.urgent
              ? initialUrgentPriorityList
              : defaults.priorities,
          statuses: defaults.statuses,
          partner_id: state.filters.partner_id,
          provider_ids: state.filters.provider_ids,
          own_tasks_only: state.filters.own_tasks_only,
        };
        return {
          tabIndex,
          filters: updatedFilters,
          draftFilters: updatedFilters,
          isInitialFilters: isEqual(defaults, updatedFilters),
          appliedFiltersCount: getAppliedFiltersCount(defaults, updatedFilters),
          draftFiltersCount: getAppliedFiltersCount(defaults, updatedFilters),
          isApplyEnabled: false,
          isResetEnabled: false,
        };
      });
    },

    isFiltersCardOpen: false,
    handleSetIsFiltersCardOpen: () => set(() => ({ isFiltersCardOpen: true })),
    handleSetIsFiltersCardClose: () =>
      set(() => ({ isFiltersCardOpen: false })),
    handleToggleFiltersCard: () =>
      set(state => ({ isFiltersCardOpen: !state.isFiltersCardOpen })),

    filters: getDefaultFilters(),
    draftFilters: getDefaultFilters(),
    isInitialFilters: true,
    isApplyEnabled: false,
    isResetEnabled: false,

    setFilters: newFilters =>
      set(state => {
        const defaults = getTabDefaults(state.tabIndex);
        const updatedFilters = { ...state.filters, ...newFilters };
        return {
          filters: updatedFilters,
          isInitialFilters: isEqual(defaults, updatedFilters),
          appliedFiltersCount: getAppliedFiltersCount(defaults, updatedFilters),
        };
      }),

    setDraftFilters: newFilters =>
      set(state => {
        const updatedDraftFilters = { ...state.draftFilters, ...newFilters };
        return {
          draftFilters: updatedDraftFilters,
          draftFiltersCount: getAppliedFiltersCount(
            getDefaultFilters(),
            updatedDraftFilters,
          ),
          isApplyEnabled: getIsApplyEnabled(state.filters, updatedDraftFilters),
        };
      }),
    applyDraftFilters: () =>
      set(state => {
        const defaults = getTabDefaults(state.tabIndex);
        const appliedFilters = {
          ...state.draftFilters,
          own_tasks_only: state.filters.own_tasks_only,
          partner_id: state.filters.partner_id,
          provider_ids: state.filters.provider_ids,
          search: state.filters.search,
          page: 1,
        };
        return {
          filters: appliedFilters,
          isInitialFilters: isEqual(defaults, appliedFilters),
          appliedFiltersCount: getAppliedFiltersCount(defaults, appliedFilters),
          isApplyEnabled: false,
          isResetEnabled: getIsResetEnabled(appliedFilters, state.tabIndex),
        };
      }),
    resetAllFilters: () => {
      const defaults = getDefaultFilters();
      return set(() => ({
        filters: defaults,
        draftFilters: defaults,
        isInitialFilters: true,
        appliedFiltersCount: 0,
        draftFiltersCount: 0,
        isApplyEnabled: false,
        isResetEnabled: false,
      }));
    },
    //this is only for filters card
    resetFilters: () => {
      const defaults = getDefaultFilters();
      return set(state => ({
        filters: {
          ...defaults,
          own_tasks_only: state.filters.own_tasks_only,
          partner_id: state.filters.partner_id,
          provider_ids: state.filters.provider_ids,
          search: state.filters.search,
        },
        draftFilters: defaults,
        isInitialFilters: true,
        appliedFiltersCount: 0,
        draftFiltersCount: 0,
        isApplyEnabled: false,
        isResetEnabled: false,
        isFiltersCardOpen: false,
        tabIndex: myWorklistTabIndex.all,
      }));
    },
    resetDraftFilters: () => {
      const defaults = getDefaultFilters();
      return set(state => ({
        draftFilters: defaults,
        draftFiltersCount: 0,
        isApplyEnabled: getIsApplyEnabled(state.filters, defaults),
      }));
    },
    syncDraftWithAppliedFilters: () =>
      set(state => ({
        draftFilters: state.filters,
        draftFiltersCount: getAppliedFiltersCount(
          getDefaultFilters(),
          state.filters,
        ),
        isApplyEnabled: false,
        isResetEnabled: getIsResetEnabled(state.filters, state.tabIndex),
      })),
    clearStore: () => {
      const defaults = getDefaultFilters();
      return set(() => ({
        tabIndex: myWorklistTabIndex.all,
        filters: defaults,
        draftFilters: defaults,
        isInitialFilters: true,
        appliedFiltersCount: 0,
        draftFiltersCount: 0,
        isFiltersCardOpen: false,
        isApplyEnabled: false,
        isResetEnabled: false,
      }));
    },
    myWorklistTabIndex: myWorklistTabIndex,
    appliedFiltersCount: 0,
    draftFiltersCount: 0,
  }),
);
